import styled from 'styled-components';

import { Text } from '@/nova/components';
import { CUST, CUST_ANIM, CUST_GRADIENTS } from '../../../../theme/customizer-tokens';

/** The intro is an opaque plate (page bg). While it co-mounts with step 1 during
 *  the dismiss/return morph, `$dimmed` cross-dissolves it as ONE element — forward
 *  it fades out (1→0) to reveal step 1; on the way back it fades in (0→1) to cover
 *  it. A pure opacity cross-dissolve (no transform) keeps it clean on both themes,
 *  fades the CTA in lock-step so the pill appears to just re-label, AND leaves the
 *  stage boxes at their true resting position so the morph measures them exactly
 *  in both directions (a transform here would offset the reverse handoff).
 *  `$inTransition` makes the whole plate non-interactive for the entire morph so a
 *  stray tap on the CTA can't advance the wizard mid-flight. */
export const Wrap = styled.div<{ $dimmed?: boolean; $inTransition?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  padding: 32px 20px 104px;
  background: ${CUST.pageBg};
  opacity: ${({ $dimmed }) => ($dimmed ? 0 : 1)};
  transition: opacity ${CUST_ANIM.MORPH_REVEAL_MS}ms ${CUST_ANIM.SCREEN_EASE};
  ${({ $inTransition }) => ($inTransition ? 'pointer-events: none;' : '')}
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 24px;
`;

export const Title = styled(Text)`
  max-width: 260px;
  margin: 0;
  font-size: 38px;
  font-weight: 900;
  line-height: 1.1;
  text-align: center;
  background-image: ${CUST_GRADIENTS.headingGradient};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

export const Subtitle = styled(Text)`
  margin: 60px 0;
  max-width: 320px;
  font-size: 15px;
  line-height: 1.35;
  text-align: center;
  color: ${CUST.introSub};
`;

export const Stages = styled.div`
  align-self: center;
  width: 100%;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const StageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 44px;
`;

/** 46×44 rounded square (Figma), radius 18 — already > height/2 so the same box
 *  flattens to a perfect pill end during the morph with no corner-pop. When the
 *  intro is leaving, the real boxes are hidden the instant the flying ghost
 *  clones take over, so they never double-paint. */
export const Circle = styled.div<{ $active: boolean; $boxesHidden?: boolean; 'data-intro-stage'?: number }>`
  flex: 0 0 auto;
  width: 46px;
  height: 44px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  font-size: 17px;
  font-weight: 700;
  background: ${({ $active }) => ($active ? CUST.accent : CUST.stageNumIdle)};
  color: ${({ $active }) => ($active ? CUST.onAccent : CUST.stageNumIdleText)};
  opacity: ${({ $boxesHidden }) => ($boxesHidden ? 0 : 1)};
`;

/** All stage labels are bold and share the title's gradient. */
export const StageLabel = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  background-image: ${CUST_GRADIENTS.headingGradient};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;